// Modules
import videojs from './vendor/videojs';

// Resources
import logo from '../../images/stornaway-logo-white.svg';
import icon_step_backward from '../../images/step-backward.svg';
import icon_fullscreen from '../../images/player-fullscreen.svg';
import icon_exit_fullscreen from '../../images/player-exit-fullscreen.svg';

// Components
import { ChoicesBar, EVENT_WILL_SELECT_CHOICE, EVENT_UNLINKED_CHOICE } from './ChoicesBar';

import { TextOverlay } from './components/text_overlay';
import { ImageOverlay } from './components/image_overlay';
import { EmbedOverlay } from './components/embed_overlay';

// Helpers
import {
  createButtons,
  isChoiceValid,
  setScene,
  getCurrentPlayer,
  getInactivePlayer,
  getLinkedVariantId,
  hideFullscreen,
  runAtTime,
  toggleFullscreen,
  getALLinkedVariantId,
  checkCaptionsVariables,
} from './helpers';

import { setupPreloadCue, setupPreloading } from './helpers/preloader';
import setupAnalytics from './analytics';
import { dispatchChoiceSelected, dispatchStoryEnd, dispatchStoryShown, dispatchVariantProgress, dispatchVariantStart } from './events';
import { DEFAULT_OPTIONS, VR_OPTIONS } from './config/options';
import { isEmbed, markStoryStart, startButton } from './helpers/player';
import { checkDeviceOrientationPermission } from './helpers/device_orientation';
import { mediaUrlOfType } from './helpers/variants';
import setupEmbed from './embed';
import { setupAdvancedLogicContext } from './helpers/advancedLogic';

if (process.env.NODE_ENV == 'development') {
  window.videojs = videojs;
}

const SELECTOR_PLAYER_LOADER = '.js-wrp-player__loader';

// Events
export const EVENT_WILL_SET_VARIANT = 'player:will-set-variant';
export const EVENT_UNLINKED_VARIANT = 'player:unlinked-variant';
const EVENT_DID_SET_VARIANT = 'player:did-set-variant';
const EVENT_REACHED_END_VARIANT = 'player:reached-end-variant';

const CLASS_POSTER_BACKGROUND = 'responsive-background';

// All variant information is stored in this object.
const variants = window.wrpVariants.variants;

export default function setupPlayer(playerEl) {
  if (hasVRVariants(variants)) {
    return import(
      /* webpackMode: "lazy" */
      /* webpackChunkName: "videojs-vr" */
      'videojs-vr'
    ).then(() => doSetupVRPlayer(playerEl));
  } else {
    doSetupPlayer(playerEl);
  }
}

function hasVRVariants(variants) {
  return Object.values(variants).some((variant) => !!variant.media_projection);
}

// Bit of a broad approach here, time allowing
// this could be restricted to older versions of iOS
// as it seems newer versions are happy with HLS
// for playing 360 videos. This may require ongoing
// maintenance though, as it seems Safari has had some
// regressions around that at times
// https://github.com/videojs/videojs-vr/issues/231
// FIXME: Temporarily disabling this for now
const USE_MP4 = false;

let sharedVolumeState = {
  volumeVal: 1,
  setVolumeVal: false,
  isVolumeControlActive: false,
};

function doSetupVRPlayer(playerEl) {
  if (USE_MP4) {
    Object.values(variants).forEach((variant) => {
      variant.media_url = mediaUrlOfType('mp4', variant) || variant.media_url;
    });
  }

  doSetupPlayer(playerEl, { vr: true });
}

function doSetupPlayer(playerEl, { vr = false } = {}) {
  if (playerEl.hasAttribute('data-analytics')) {
    setupAnalytics({
      viewingId: process.env.NODE_ENV == 'development' && playerEl.getAttribute('data-analytics') !== 'true' ? playerEl.getAttribute('data-analytics') : null,
      projectId: playerEl.getAttribute('data-projectId'),
    });
  }

  // Disables videoJS default styles.
  window.VIDEOJS_NO_DYNAMIC_STYLE = true;

  let currentVariant;
  // let previousVariant; // no longer using in History
  let queuedVariant;

  // Initialzing history.
  let viewingHistory = [];

  // Removing resetHistory from restart but saving for manual resets later 21/7/24
  // const resetHistory = () => {
  //   viewingHistory.length = 0; // Keep the reference to the viewing history
  // };

  const addToHistory = (variantId) => {
    viewingHistory.push(variantId);
  };

  const removeFromHistory = () => {
    viewingHistory.pop();
  };

  // Clears poster image after first video so it doesn't appear
  // when preloading is slow or when skipping
  $(document).on(EVENT_DID_SET_VARIANT, clearPosterImageAfterFirstPlay);

  function clearPosterImageAfterFirstPlay(event) {
    if (!event.detail.isStart) {
      getCurrentPlayer(videojs.getAllPlayers()).el().classList.remove(CLASS_POSTER_BACKGROUND);
      $(document).off(EVENT_DID_SET_VARIANT, clearPosterImageAfterFirstPlay);
    }
  }

  /**
   * Sets player variant and updates UI as needed. Used mostly on user interaction.
   * @param {Object} player - A videoJS player instance.
   * @param {Object} variant - Used to update parts of UI as needed.
   * @param {Object} options - Optional values that denote the intention when setting a variant.
   **/
  function setVariant(player, variant, { isStart, isFirstPlay, autoplay = false, isChoiceValid = true } = {}, goToTime = '0') {
    currentVariant = currentVariant || variant;
    queuedVariant = variant;

    // Allow player to notify that a new variant will be set
    // so `runAtTime` can clean up
    player.trigger(EVENT_WILL_SET_VARIANT, { variant });

    if (goToTime !== '0' && player.isVideo) {
      player.currentTime(parseFloat(goToTime));
    }

    let invalidChoice = !isChoiceValid;

    // TODO: Activate this if autoplay should be on from the start variant.
    // Will autoplay if its the start variant, otherwise it will rely on the autoplay property.
    // autoplay = isStart || autoplay ;
    // Store the variant played by the player on it
    player.variant = queuedVariant;
    // Sets the player source to play the variant's media URL.
    if ((queuedVariant.is_placeholder && queuedVariant.image) || (queuedVariant.is_placeholder && queuedVariant.embed_url)) {
      setScene(player, null);
    } else {
      setScene(player, queuedVariant.media_url, autoplay, isFirstPlay);
    }

    // Title only requires updates on the watch page, embeds do not include them.
    if (!isEmbed(player)) updateVideoTitle(queuedVariant);

    // Populate choices bar with the variant's choices.
    player.choicesBar.update(queuedVariant, autoplay, true);

    if (!player.isVideo) {
      // If the first player is not a video player, show all choices immediately
      player.choicesBar.showAllChoicesForNonTimedVariants();
    }

    // An invalid choice determines the end of the story, it will empty the choices bar and mark the story as the end.
    // Choices will still render if text is provided to them, this is expected behaviour.
    if (invalidChoice) {
      player.choicesBar.empty();
    }

    // This event is used to know if video sources require updating.
    const setVariantEvent = new CustomEvent(EVENT_DID_SET_VARIANT, {
      bubbles: true,
      detail: {
        isStart,
      },
    });

    player.el().dispatchEvent(setVariantEvent);
  }

  /**
   * Returns a variant if the id provided matches any on the variants object.
   *
   * variantId [string]: A variant id to be looked up, e.g 'd1111a26'.
   **/
  function lookupVariant(variantId) {
    return variants[variantId];
  }

  /**
   * Find the default choice, returns first result.
   *
   * choices [array]: Array of choices to look for a default one.
   **/
  function lookupDefaultChoice(choices) {
    return choices.filter((choice) => choice.default)[0];
  }

  /**
   * Finds the respective DOM element and updates it with the variant name.
   *
   * variant [object]: Used to access the name property and update the DOM.
   **/

  function updateVideoTitle(variant) {
    const SELECTOR_VARIANT_HEADING = '.js-island-name';
    const element = document.querySelector(SELECTOR_VARIANT_HEADING);
    if (element) {
      element.innerHTML = variant.island_name;
    }
  }

  function updateCurrentVariant() {
    // previousVariant = currentVariant;
    currentVariant = queuedVariant;
  }

  /**
   * Sets variant to a default choice.
   *
   * @param {Object} player A videoJS player instance.
   * @param {Object} variant Used to access the choices property.
   **/

  /**
   * Sets variant to previously watched one, can backtrack to the first variant. Uses history to find which variant to set to.
   *
   **/
  function updateSkipBack(player) {
    // Set variant prior to removing the item.
    if (viewingHistory.length >= 1) {
      const variantId = viewingHistory[viewingHistory.length - 1];

      setVariant(player, lookupVariant(variantId), {
        goingBack: true,
      });
      if (!player.isVideo) {
        dispatchVariantStart(player);
      }

      updateCurrentVariant();
      removeFromHistory();
    }
  }

  // Initialization options for a videoJS player.
  // These sometimes do not get applied in Chrome, so they also get injected in the video tag, see player partial.
  const options = {
    ...DEFAULT_OPTIONS,
    currentVariant: currentVariant,
    html5: {
      ...(DEFAULT_OPTIONS.html5 || {}), // preserve any existing html5 config
      nativeTextTracks: false,
    },
  };

  options.html5.vhs.limitRenditionByPlayerDimensions = !hasVRVariants(variants);

  // Two players instances are used, one visible and another hidden.
  // artemis is the default current player.
  const artemis = videojs('#js-video-artemis', options, () => {
    onPlayerReady(artemis, true);
    // Re-set the poster image that'll have been cleared by the `player.reset()`
    // inside `setScene`.
    // TODO: Maybe find a better way to handle the reset, though it may make sense
    // that the poster is set programmatically here rather than automatically as the
    // video source is not set automatically either.
    // TODO: Review the clearing of the poster image after first play,
    // the `player.reset()` may make it unnecessary now
    artemis.poster(document.getElementById('js-video-artemis').getAttribute('poster'));
  });
  const apollo = videojs('#js-video-apollo', options, () => onPlayerReady(apollo));

  const aLContext = setupAdvancedLogicContext([artemis, apollo]);

  // Facilitates access to players.
  const players = {
    artemis,
    apollo,
  };

  if (isEmbed(artemis)) {
    setupEmbed(players, aLContext);
  }

  // Expose player to window object when in development mode.
  if (process.env.NODE_ENV == 'development') {
    window.players = players;
  }

  // Function to check context variables and initialize sharedCaptionState
  let sharedCaptionState = {
    activeTrack: null, // Initial value
  };

  const { variableCaptionsLabel, variableShowCaptions } = checkCaptionsVariables(aLContext);
  if (variableShowCaptions && variableCaptionsLabel) {
    sharedCaptionState.activeTrack = { label: variableCaptionsLabel };
  }

  // Check if show_captions exists
  const showCaptionsExists = variableShowCaptions !== undefined;

  // Run Behaviours on unlinked buttons - especially useful for toggle buttons
  document.addEventListener(EVENT_UNLINKED_CHOICE, (event) => {
    // Prevents event from running multiple times in a row.
    event.stopImmediatePropagation();

    // Get the choice details from the event to run behaviours
    const selectedUnlinkedChoice = event.detail.unlinkedChoice;
    aLContext.runBehavioursBeforeClick(selectedUnlinkedChoice);

    const allPlayers = videojs.getAllPlayers();
    const currentPlayer = getCurrentPlayer(allPlayers);
    const hiddenPlayer = getInactivePlayer(allPlayers);
    let goToTime = event.detail.goToTime;
    if (currentPlayer.isVideo && goToTime !== '0') {
      currentPlayer.currentTime(parseFloat(goToTime));
    }

    // Check if a Linked Choice has been selected, if not then run the default choice
    if (!currentPlayer.choicesBar.selectedChoice) {
      // Get the Default Choice to cue up the default progress behaviour
      let defaultChoice = lookupDefaultChoice(currentVariant.choices);

      // Also run the Pre-logic behaviours for the default choice
      aLContext.runBehavioursBeforeClick(defaultChoice);

      let variantId;

      // Get Variant ID of the default Variant, whether game logic or direct choice
      if (defaultChoice.conditional_logic_block_present) {
        variantId = getALLinkedVariantId({ choice: defaultChoice, aLContext });
      } else {
        // The id of the next variant. It will play a direct choice or the result of a conditional choice
        variantId = getLinkedVariantId({ choice: defaultChoice, history: viewingHistory });
      }

      // Run Post-logic Game Logic behaviours for the default choice
      aLContext.runBehavioursOnClick(defaultChoice);

      // If default choice does not link anywhere, see it as an ending.
      if (
        variantId === null ||
        // NB choices which use advanced logic blocks DO NOT
        // sideload linked_variants directly;
        // instead linked_variants are found within them
        // advanced logic blocks
        (!defaultChoice.conditional_logic_block_present && Object.values(defaultChoice.linked_variants).length === 0)
      ) {
        currentPlayer.isPlayingEnd = true;
      }

      // If it's not an ending, then send Default Choice linked variant to the hidden player
      // Swapping players will then be handled by the player ended event unless another button is clicked
      if (!currentPlayer.isPlayingEnd) {
        const nextVariant = variantId ? lookupVariant(variantId) : null;
        setVariant(hiddenPlayer, nextVariant, {
          isChoiceValid: isChoiceValid(defaultChoice),
          autoplay: false,
          isStart: variantId === window.wrpVariants.start_variant,
        });
      }
    }
  });

  // Potentially load the variant on the hidden player here.
  document.addEventListener(EVENT_WILL_SELECT_CHOICE, (event) => {
    // Prevents event from running multiple times in a row.
    event.stopImmediatePropagation();
    // Find innactive player (where currentPlayer === false, there are other players which will return a falsy value).
    // Or check only between artemis and apollo
    // set variant with that player, will need a choice to be passed to it.
    const allPlayers = videojs.getAllPlayers();
    const currentPlayer = getCurrentPlayer(allPlayers);
    const hiddenPlayer = getInactivePlayer(allPlayers);

    let choiceDiv = $(event.target).closest('.js-select-variant');
    let goToTime = choiceDiv.attr('data-go-to-time');

    // Sets default choice, unless a selected choice is available.
    let choice = lookupDefaultChoice(currentVariant.choices);

    // Used to to sure to set a choice as selected if available.
    let selectedChoice = null;

    if (event.detail != null && event.detail.selectedChoice != null) {
      selectedChoice = event.detail.selectedChoice;
      currentPlayer.choicesBar.selectedChoice = selectedChoice;
      hiddenPlayer.choicesBar.selectedChoice = null;
    }

    if (currentPlayer.choicesBar.selectedChoice) {
      choice = currentPlayer.choicesBar.selectedChoice;
    }

    // behaviours
    aLContext.runBehavioursBeforeClick(choice);

    let variantId;
    if (choice.conditional_logic_block_present) {
      variantId = getALLinkedVariantId({ choice, aLContext });
    } else {
      // The id of the next variant. It will play a direct choice or the result of a conditional choice.
      variantId = getLinkedVariantId({ choice, history: viewingHistory });
    }

    // behaviours
    aLContext.runBehavioursOnClick(choice);

    // Choices that do not link anywhere are seen as an ending.
    if (
      variantId === null ||
      // NB choices which use advanced logic blocks DO NOT
      // sideload linked_variants directly;
      // instead linked_variants are found within them
      // advanced logic blocks
      (!choice.conditional_logic_block_present && Object.values(choice.linked_variants).length === 0)
    ) {
      currentPlayer.isPlayingEnd = true;
    }

    // skipping if no variant id and using advanced logic
    if (!choice.conditional_logic_block_present || variantId) {
      dispatchChoiceSelected(currentPlayer, {
        choice: choice,
        destination: variantId ? lookupVariant(variantId) : choice.link?.external_url,
        type: variantId ? 'island' : choice.link?.external_url ? 'external' : null,
      });
    }

    if (!currentPlayer.isPlayingEnd) {
      const nextVariant = variantId ? lookupVariant(variantId) : null;
      setVariant(hiddenPlayer, nextVariant, {
        isChoiceValid: isChoiceValid(choice),
        autoplay: false,
        isStart: variantId === window.wrpVariants.start_variant,
        goToTime: goToTime,
      });
    }

    if (!currentPlayer.isVideo && !event.detail.is_selecting_default) {
      playSelectedVariant(currentPlayer, { waitForHiddenPlayback: true }, goToTime);
      return;
    } else if (currentPlayer.ended()) {
      // Handle clicks after video ended
      playSelectedVariant(currentPlayer, { waitForHiddenPlayback: true }, goToTime);
      return;
    } else {
      const cutOnClick = choiceDiv.attr('data-cut-on-click');
      if (cutOnClick == 'true') {
        // Grab the current player before setting the timeout
        // so we have a reference to the current player at the time of click
        // In case the video ends within the duration of the timeout and players get swapped
        const hiddenPlayer = getInactivePlayer(allPlayers);

        // give the hidden player a 750ms head start before cutting
        // to try and avoid too much loading
        setTimeout(() => {
          const allPlayers = videojs.getAllPlayers();
          const currentPlayerAfterClick = getCurrentPlayer(allPlayers);
          // The player might have finished playing the previous video
          // and started playing the new one already so we don't want
          // to swap again
          if (currentPlayerAfterClick == currentPlayer) {
            updateCurrentVariant();
            swapPlayers(
              currentPlayer,
              hiddenPlayer,
              {
                waitForHiddenPlayback: true,
              },
              goToTime,
            );
            // Pause the current player as we don't want
            // it carrying on playing with sound in the background
            currentPlayer.pause();
          }
        }, 750);
      }
    }
  });

  /**
   * Sets up a player with control and choices bar.
   * Sets other defaults a player should have.
   * @param {Object} player - A videoJS player instance.
   * @param {Boolean} currentPlayer - Determines which player is in use. It changes as variants are set.
   */
  function onPlayerReady(player, currentPlayer = false) {
    // Initialize the VR player early so as much of the 3D setup
    // is already done and it does not slow down the transitions
    if (player.vr) {
      player.vr({
        ...VR_OPTIONS,
        motionControls:
          window.wrpVariants.use_device_orientation &&
          // Android has an issue with the starting orientation of the video
          // when the device controls take over: https://github.com/videojs/videojs-vr/issues/139
          // ThreeJS seems to provide some parameters to account for that: https://github.com/mrdoob/three.js/pull/13000
          // However, the initialisation of the DeviceOrientationControls is burried deep inside
          // the vr plugin :'( https://github.com/videojs/videojs-vr/blob/eb80c8a55909be14a80ae93dfa295b7266f5aff4/src/orbit-orientation-controls.js#L56
          // It may be more suitable to disable the plugin's motion control and instanciate the ThreeJS native control
          // on our side.
          // Whether adjusting the current control or setting up our own would require figuring when
          // the 3D scene is actually ready as:
          // - it gets re-initialized every time a media is loaded in the player: https://github.com/videojs/videojs-vr/blob/eb80c8a55909be14a80ae93dfa295b7266f5aff4/src/plugin.js#L106
          // - the `initialized` event fires before the controls are actually set due to the promise checking for VR displays: https://github.com/videojs/videojs-vr/blob/eb80c8a55909be14a80ae93dfa295b7266f5aff4/src/plugin.js#L700
          // ThreeJS actually went and removed the Control from their codebase as of r134: https://github.com/mrdoob/three.js/pull/22654
          !videojs.browser.IS_ANDROID,
      });
      player.mediainfo = player.mediainfo || {};
      // Initialize the player early so that `setScene`
      // has the appropriate 3D scene set up already
      // for handling `changeProjection`
      player.vr().init();

      // To enably the device orientation controls on iOS 13+
      // we need to ask permission from the users. To provide
      // a better experience, we want it to only happen after
      // users click the Play button to start their video.
      // However, so they don't miss anything of the video
      // while the popup appears and they consider their choice,
      // we need to pause the video while this happen.
      // This requires some careful synchronisation between the player
      // starting playing and the permission check returning (which may not even
      // trigger a popup if they've already been accepted).
      if (currentPlayer && window.wrpVariants.use_device_orientation) {
        // First we need to wait for the VR plugin to have replaced the BigPlayButton
        // with its own before registering our own listener
        player.vr().on('initialized', () => {
          player.one(startButton(player), ['tap', 'click'], () => {
            // iOS requires that the permission check happens on user interaction
            // and not from player events, so we can't trigger it on `play`. This means
            // we'll need to track
            let permissionsChecked;

            // We're not expecting any return from this
            // eslint-disable-next-line promise/catch-or-return, promise/always-return
            checkDeviceOrientationPermission(videojs.browser).then(() => {
              permissionsChecked = true;
              player.play();
            });
            // Pausing need to happen only after the player starts playing
            // to be effective
            player.one('play', () => {
              if (!permissionsChecked) {
                player.pause();
              }
            });
          });
        });
      }

      // Remove the cardboard button automatically inserted by the plugin
      // on iOS and Android as this would require fullscreen to be supported
      // This needs to happen on `loadedmetadata` to make sure it's not reset
      // by loading future media
      player.on('loadedmetadata', () => {
        player.controlBar.removeChild('CardboardButton');
      });
    }

    // Track when player first plays so we can notify
    // the start of an island
    player.on('firstplay', () => {
      dispatchVariantStart(player);
    });

    // Dispatch progress events as they happen, any throttling
    // is left to the listener of the events
    player.on('timeupdate', () => {
      dispatchVariantProgress(player, {
        current: player.currentTime(),
        total: player.duration(),
        unit: 's',
      });
    });

    player.on('ended', () => {
      if (player.isPlayingEnd) {
        dispatchStoryEnd(player);
      }
    });

    // Creates controlBar and choicesBar for player.
    createControlBar(player);
    let startVariant;
    if (currentPlayer) {
      startVariant = window.wrpVariants.variants[window.wrpVariants.start_variant];
    }

    // Create image overlay behind text and choices
    createImageOverlay(player, startVariant);
    // Create embed overlay behind text and choices
    createEmbedOverlay(player, startVariant);
    // Create placeholder before choices bar so it appears
    // below split buttons and such, just like the video content would
    createTextOverlay(player, startVariant, aLContext);

    const choicesBar = createChoicesBar(player, startVariant, aLContext);
    player.choicesBar = choicesBar;
    player.currentPlayer = currentPlayer;

    // Sets up preloading logic and event listeners.
    setupPreloadCue(player);

    player.ready(function () {
      const volumeControl = player.controlBar.volumePanel.volumeControl.el();

      volumeControl.addEventListener('mouseover', function () {
        sharedVolumeState.isVolumeControlActive = true;
      });

      volumeControl.addEventListener('mouseout', function () {
        sharedVolumeState.isVolumeControlActive = false;
      });
    });

    player.on('volumechange', function () {
      if (sharedVolumeState.isVolumeControlActive) {
        sharedVolumeState.setVolumeVal = true;
        sharedVolumeState.volumeVal = player.volume();
      }
    });

    player.on('loadeddata', function () {
      if (sharedVolumeState.setVolumeVal) {
        player.volume(sharedVolumeState.volumeVal);
      }
    });

    // Keeps track of when a variant is set.
    // Used in preloading players, needs to run before initial setVariant.
    player.on(EVENT_DID_SET_VARIANT, () => {
      setupPreloading(player, variants, viewingHistory);

      // A few seconds from the end, makes sure the default variant is mounted on the player.
      const secondsFromEnd = 2;
      if (
        (!player.choicesBar.selectedChoice || player.choicesBar.unlinkedChoice) &&
        player.variant.end_behavior !== 'pause' // Only preselect default choice if we don't pause at end
      ) {
        runAtTime({
          player,
          displayAt: secondsFromEnd,
          callback: () => {
            // When an end variant is reached an event is issued.
            const allChoicesAreInvalid = currentVariant.choices.every(
              // TODO: Double check that one, linked_variants is a hash
              // so `length` will be empty
              (choice) => choice.linked_variants.length,
            );

            if (allChoicesAreInvalid) {
              const reachedEndEvent = new CustomEvent(EVENT_REACHED_END_VARIANT, {
                bubbles: true,
              });
              event.target.dispatchEvent(reachedEndEvent);
            }

            // Checking again if a choice was selected prevents from a default to be put in place.
            if (!player.choicesBar.selectedChoice && currentVariant.end_behavior !== 'pause') {
              // Dispaches that a choice is being made.
              const choiceMadeEvent = new CustomEvent(EVENT_WILL_SELECT_CHOICE, {
                bubbles: true,
                detail: {
                  is_selecting_default: true,
                },
              });

              event.target.dispatchEvent(choiceMadeEvent);
            }
          },
          runOnce: true,
        });
      }
    });

    // When user selects a track, update the shared caption state and context
    player.textTracks().on('change', function () {
      let textTracksArray = Array.from(player.textTracks());

      // Find the active track (if any)
      let activeTrack = textTracksArray.find(function (track) {
        return track.mode === 'showing';
      });

      const { variableCaptionsLabel } = checkCaptionsVariables(aLContext);

      // Update sharedCaptionState to the label of the active track
      if (activeTrack) {
        sharedCaptionState.activeTrack = { label: activeTrack.label };

        // Update the captions_label variable to the active track label if it exists
        if (variableCaptionsLabel !== null) {
          aLContext.processSetBehaviours([
            {
              variables: ['captions_label'],
              value: sharedCaptionState.activeTrack.label,
            },
          ]);
        }

        // If the show_captions variable exists, update it to true
        if (showCaptionsExists) {
          aLContext.processSetBehaviours([
            {
              variables: ['show_captions'],
              value: 'true',
            },
          ]);
        }
        sharedCaptionState.captionsNowShowing = true;
      } else {
        let allTracksDisabled = textTracksArray.every((track) => track.mode === 'disabled');

        if (allTracksDisabled && sharedCaptionState.captionsNowShowing) {
          sharedCaptionState.activeTrack = null;
          sharedCaptionState.captionsNowShowing = false; // Reset for this session

          if (showCaptionsExists) {
            aLContext.processSetBehaviours([
              {
                variables: ['show_captions'],
                value: 'false',
              },
            ]);
          }
        }
      }
    });

    player.on('loadeddata', function () {
      if (player.variant.captions) {
        player.variant.captions.sort((captionA, captionB) => captionA.language_label.localeCompare(captionB.language_label));

        // Add tracks to the player on load
        player.variant.captions.forEach((caption) => {
          const track = {
            kind: 'captions',
            src: caption.source,
            srclang: caption.language_code,
            label: caption.language_label,
          };

          player.addRemoteTextTrack(track);
        });
      }
      let playerTextTracks = player.textTracks();

      const { variableCaptionsLabel, variableShowCaptions } = checkCaptionsVariables(aLContext);

      sharedCaptionState.captionsNowShowing = false;

      function updateCaptionsOnLoad() {
        let captionsWereEnabled = false;
        for (let i = 0; i < playerTextTracks.length; i++) {
          let trackItem = playerTextTracks[i];
          trackItem.mode = 'disabled'; // Disable all tracks to start with

          // Check first if there's a show_variable variable set for captions & set that
          if (variableShowCaptions) {
            if (variableCaptionsLabel && trackItem.label === variableCaptionsLabel) {
              trackItem.mode = 'showing';
              captionsWereEnabled = true;
              // If there's no variable set for captions, check if there's an active track that matches available labels
            } else if (!variableCaptionsLabel && sharedCaptionState.activeTrack && trackItem.label === sharedCaptionState.activeTrack.label) {
              trackItem.mode = 'showing';
              captionsWereEnabled = true;
            }
          }
        }

        sharedCaptionState.captionsNowShowing = captionsWereEnabled;

        // Show the CC button if there are captions available
        if (player.controlBar.captionsButton) {
          player.controlBar.captionsButton.show();
        }

        // Update caption tracks with changes
        player.textTrackSettings.updateDisplay();
      }

      // Run immediately on first island
      updateCaptionsOnLoad();

      // When new tracks added, update captions
      playerTextTracks.addEventListener('addtrack', function () {
        updateCaptionsOnLoad();
      });
    });

    player.on(EVENT_REACHED_END_VARIANT, (event) => {
      const player = event.detail.player;
      player.isPlayingEnd = true;
    });

    // Sets inital player state with start variant.
    if (currentPlayer) {
      let variantId = window.wrpVariants.start_variant;
      setVariant(player, lookupVariant(variantId), {
        isStart: true,
        autoplay: window.wrpVariants.autoplay_start, // This is the setting to toggle the first time it plays - and also add mute in helper setScene
        isFirstPlay: true, // This tells the player that it's the first time it's playing the start island. Stops the start island autoplaying too soon (and muting) if the user is linked back to it
      });

      // Removes loading element on top of player.
      const loaderElement = document.querySelector(SELECTOR_PLAYER_LOADER);
      if (loaderElement) loaderElement.remove();

      // Adds start variant id to history and also Start flag
      const startFlag = 'First island played on this viewing:';
      addToHistory(startFlag);
      addToHistory(currentVariant.id);

      // Dispatch 'sw.story.shown' event for API
      dispatchStoryShown(player);
    }

    // Placed to prevent native fullscreen from activating with other gestures such as swipe or touch.
    if (videojs.browser.IS_ANDROID || videojs.browser.IS_IOS) {
      player.on('fullscreenchange', function () {
        if (player.isFullscreen()) {
          player.exitFullscreen();
        }
      });
    }

    // Adds a class that a story has started playing.
    // This is used to understand to discern from a player starting and the story itself
    // as the control bar is not meant to be revealed as the player plays/pauses.
    const bigPlayButton = player.el().querySelector('.vjs-big-play-button');
    player.one('play', () => {
      markStoryStart(player);
    });
    // TODO: Move those two to the BigPlayButton "Handle click"
    // When not playing videos, we can't mark the story as starting on the `play` event
    // Need to listen to the 'tap' event for touch browsers
    // as VideoJS prevents `touchend` to create its own `tap`
    // which in turn prevents `click` to be sent by the browser :(
    player.one(bigPlayButton, ['tap', 'click'], () => {
      markStoryStart(player);
    });

    // Logic to handle what happens when the video ends
    // Safari has stopped detecting the ended event on the video element
    // so we need to use the timeupdate event to detect when the video is near the end
    let hasEnded = false;
    let hasLoggedDuration = false;

    function markEndedAndRunHandler() {
      // Prevents the event from running multiple times in a row.
      if (hasEnded) {
        return;
      }

      hasEnded = true;
      onVideoEnded();
    }

    player.on('timeupdate', () => {
      const current = player.currentTime();
      const total = player.duration();

      if (!hasLoggedDuration && total) {
        hasLoggedDuration = true;
      }

      dispatchVariantProgress(player, {
        current,
        total,
        unit: 's',
      });

      if (total && current >= total - 0.3) {
        markEndedAndRunHandler();
      }
    });

    // Native ended event fallback
    player.on('ended', () => {
      markEndedAndRunHandler();
    });

    player.on('play', () => {
      // Reset the ended state when the video starts playing
      hasEnded = false;
    });

    function onVideoEnded() {
      const allPlayers = videojs.getAllPlayers();
      const currentPlayer = getCurrentPlayer(allPlayers);
      const hiddenPlayer = getInactivePlayer(allPlayers);
      if (
        player.variant.end_behavior == 'pause' &&
        // Only pause if user has not made a selection
        // `queuedVariant` cannot be used as the default choice
        // gets queued automatically if nothing was selected
        !player.choicesBar.selectedChoice
      )
        return;

      // Preloads the selected variant in the hidden player
      maybePreloadDefaultVariant(currentPlayer, hiddenPlayer);

      playSelectedVariant(currentPlayer, {
        waitForHiddenPlayback: true,
      });
    }
  }

  function maybePreloadDefaultVariant(player, hiddenPlayer) {
    if (!player.choicesBar.selectedChoice && !hiddenPlayer.src() && hiddenPlayer.isVideo) {
      const defaultChoice = lookupDefaultChoice(currentVariant.choices);
      const variantId = getLinkedVariantId({
        choice: defaultChoice,
        history: viewingHistory,
      });
      // Variant is set on the player.
      setVariant(hiddenPlayer, lookupVariant(variantId));
    }
  }

  function swapPlayers(player, hiddenPlayer, { waitForHiddenPlayback = false } = {}, goToTime = '0') {
    if (!player.isPlayingEnd) {
      player.currentPlayer = !player.currentPlayer;
      hiddenPlayer.currentPlayer = !hiddenPlayer.currentPlayer;

      // Tag that the players are currently being swapped
      // This allows to properly hide the controls during the swap
      // as VideoJS sometimes flashes them again when the loading
      // takes a little long on slow connections
      player.el().classList.add('vjs-wrp--swapping');
      hiddenPlayer.el().classList.add('vjs-wrp--swapping');

      if (waitForHiddenPlayback && hiddenPlayer.isVideo) {
        // Wait for the hiddenPlayer to actually have started playing
        // before swapping the players, to ensure seamlessness
        hiddenPlayer.one('play', () => {
          hiddenPlayer.one('timeupdate', () => {
            doSwapPlayers();
          });
        });
      } else {
        doSwapPlayers();
        // Non video players will run immediately
        // so we need to dispatch the event. There might still
        // be video players that'll swap
        if (!hiddenPlayer.isVideo) {
          dispatchVariantStart(hiddenPlayer);
        } else {
          // If the hidden player is not a video player, show all choices immediately
          hiddenPlayer.choicesBar.showAllChoicesForNonTimedVariants();
        }
      }
      if (hiddenPlayer.isVideo) {
        if (goToTime) {
          hiddenPlayer.currentTime(goToTime);
        }
        hiddenPlayer.play();
      }
    } else {
      // Resets playing end in case another choice is picked on video restart.
      player.isPlayingEnd = false;
      hiddenPlayer.isPlayingEnd = false;
    }

    function doSwapPlayers() {
      player.el().classList.toggle('js-wrp-video-hidden');
      hiddenPlayer.el().classList.toggle('js-wrp-video-hidden');
      player.el().classList.remove('vjs-wrp--swapping');
      hiddenPlayer.el().classList.remove('vjs-wrp--swapping');

      // Clears choices state for hidden and current player.
      // As variants are played both require a reset.
      // TODO: Feels like only the old player should reset its choices
      // and even then, it feels like it should be the responsibility of
      // the ChoicesBar itself rather than the player swapping
      resetChoicesState({
        player: hiddenPlayer,
        currentVariant,
      });
      resetChoicesState({
        player,
        currentVariant,
      });
      resetEmbedOverlayState({
        player,
      });
    }

    // Resets or adds to history after a variant is played.
    // Skipping will also update history. See updateSkipNext, updateSkipBack.
    const isStartVariant = currentVariant.id === window.wrpVariants.start_variant;
    const restartFlag = 'Replaying Start Island - Pass Go and Collect £200:';
    // Updates player history when a variant is set to play next.
    if (isStartVariant) {
      // No longer clears history if a start variant is set - adds restart flag
      // resetHistory();
      addToHistory(restartFlag);
      addToHistory(currentVariant.id);
    } else {
      addToHistory(currentVariant.id);
    }
  }

  function playSelectedVariant(player, { waitForHiddenPlayback = false } = {}, goToTime = '0') {
    updateCurrentVariant();

    const allPlayers = videojs.getAllPlayers();
    const hiddenPlayer = getInactivePlayer(allPlayers);

    // Hide the choices straight away, only if playing a video
    // TODO: Review why this happens
    // if (player.isVideo) {
    //   player.choicesBar.hideChoices();
    // }

    // This handles what happens when the player autoplays a default choice
    // Reveal the choices straight away if the player is not a video player
    // otherwise hide them to let them be revealed when necessary
    // TODO: Move this reponsibility to the ChoicesBar
    if (!hiddenPlayer.isVideo) {
      hiddenPlayer.choicesBar.showAllChoicesForNonTimedVariants();
    } else {
      // hiddenPlayer.choicesBar.hideChoices();
    }
    // And the timer too
    player.choicesBar.hideTimer();
    hiddenPlayer.choicesBar.hideTimer();

    // Adds a class that a story has started playing.
    // This is used to understand to discern from a player starting and the story itself
    // as the control bar is not meant to be revealed as the player plays/pauses.
    const storyStartedClass = 'wrp-player-story-started';
    if (!player.el().classList.contains(storyStartedClass)) {
      player.el().classList.add(storyStartedClass);

      const allPlayers = videojs.getAllPlayers();
      const hiddenPlayer = getInactivePlayer(allPlayers);

      hiddenPlayer.el().classList.add(storyStartedClass);
    }

    // Sets variant to default if one hasn't been set yet.
    // Fallback for when video might not hit marker that triggers setting the default variant.
    // Experienced in Safari Desktop (intermittent issue on v13.1).
    if (player.isVideo) {
      maybePreloadDefaultVariant(player, hiddenPlayer);
    }

    swapPlayers(player, hiddenPlayer, { waitForHiddenPlayback }, goToTime);
  }

  /**
   * Resets choices state of buttons. Useful when a video ends.
   * @param {Object} player A videoJS player.
   * @param {Object} currentVariant The current variant being played.
   */
  const resetChoicesState = ({ player }) => {
    // TODO: Abstract clearing button styles to its own function.
    // Similar functionality is present in ChoicesBar.
    // Clears selected state from choices.
    const buttons = player.el().querySelectorAll('.vjs-wrp-button');

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', false);
      button.removeAttribute('disabled');
    });
  };

  /**
   * Resets choices state of buttons. Useful when a video ends.
   * @param {Object} player A videoJS player.
   * @param {Object} currentVariant The current variant being played.
   */
  const resetEmbedOverlayState = ({ player }) => {
    // TODO: Abstract clearing Embed Overlay URL to its own function.

    const embed_overlay = player.el().querySelector('.embed-iframe');

    if (embed_overlay) {
      embed_overlay.remove();
    }
  };

  /**
   * Instatiates a choices bar with the start variant. It gets appended to a controlBar.
   * @param {object} player VideoJS player instance.
   **/
  function createChoicesBar(player, variant, aLContext) {
    const choicesBar = new ChoicesBar(player, {
      variant: variant,
      history: viewingHistory,
      variants,
      aLContext,
    });

    player.addChild(choicesBar, {}, 1);
    return choicesBar;
  }

  function createImageOverlay(player, variant) {
    const component = new ImageOverlay(player, { variant });
    player.addChild(component);
  }

  function createTextOverlay(player, variant, aLContext) {
    const component = new TextOverlay(player, { variant, aLContext });
    player.addChild(component);
  }

  function createEmbedOverlay(player, variant) {
    const component = new EmbedOverlay(player, { variant });
    player.addChild(component);
  }

  /**
   * Creates and appends custom control buttons to player's control bar.
   * @param {object} player VideoJS player instance.
   */
  function createControlBar(player) {
    // if relative-player-images meta tag is set use the meta tag relative paths for the player images
    // primarily used by the Standalone Player
    const playerSpecificMetaTag = document.querySelector('meta[name="relative-player-images"]');
    const playerSpecificImages = playerSpecificMetaTag ? JSON.parse(playerSpecificMetaTag.content) : {};

    // Creates a logo button and wraps it in an url when available.
    // This is added to the buttons url to be part of the control bar.
    let logo_button = videojs.dom.createEl('img', {
      src: playerSpecificImages.logo || logo,
      alt: 'Stornaway.io',
      className: 'vjs-wrp-control-logo-button',
    });

    // Wraps logo in an link if one is available.
    if (window.public_watch_url) {
      const logo_url = videojs.dom.createEl('a', {
        href: window.public_watch_url,
        target: '_blank',
        className: 'vjs-wrp-control-logo-button',
      });

      // Removes class from the button since the anchor tag is the parent now.
      logo_button.classList.remove('vjs-wrp-control-logo-button');

      // The logo is added a child of the link.
      logo_url.appendChild(logo_button);

      // The button passed is the one with the link around the logo.
      logo_button = logo_url;
    }

    // TODO: Render this using the buttons array below. ⬇️
    player.getChild('controlBar').addChild('SubsCapsButton', {});

    /*
     * This object is used to render a set of buttons in videoJS's control bar, including the product's logo, skipping and more.
     *
     */
    const buttons = [
      !vr && {
        el: videojs.dom.createEl(
          'img',
          {
            src: playerSpecificImages.icon_step_backward || icon_step_backward,
            alt: 'Back to previous scene',
            className: 'vjs-wrp-control-step-backward',
          },
          {
            role: 'button',
          },
        ),
        callback: () => updateSkipBack(player),
      },
      // TODO: Fullscreen disabled for now.
      // Work on a native fullscreen mode with the fullscreen api that does not disrupt the players being revealed and hidden when switching variants.
      !hideFullscreen() && {
        el: videojs.dom.createEl(
          'img',
          {
            src: playerSpecificImages.icon_fullscreen || icon_fullscreen,
            alt: 'Enter fullscreen mode',
            className: 'vjs-wrp-fullscreen',
          },
          { role: 'button' },
        ),
        callback: () => {
          toggleFullscreen(true);
        },
      },
      !hideFullscreen() && {
        el: videojs.dom.createEl(
          'img',
          {
            src: playerSpecificImages.icon_exit_fullscreen || icon_exit_fullscreen,
            alt: 'Exit fullscreen mode',
            className: 'vjs-wrp-fullscreen hidden',
          },
          { role: 'button' },
        ),
        callback: () => {
          toggleFullscreen(false);
        },
      },
      {
        el: logo_button,
      },
    ].filter(Boolean); // VideoJS doesn't like non object values

    createButtons(player.controlBar, buttons);

    player.controlBar.on('click', (event) => {
      event.stopPropagation();
    });
  }
}
